var AttributionTruthLedgerEvents = (function() {
  'use strict';

  var ATTR_DEBUG = false;

  function log() {
    if (ATTR_DEBUG) console.log.apply(console, arguments);
  }

  /**
   * Determine paid flag based on attribution data
   * Returns: 1 (retained paid signal), 0 (no paid signal), 'uncertain'
   */
  function getPaidFlag() {
    try {
      var stored = AttributionStorage.read();
      if (!stored) return 'uncertain';
      var google = stored.last_google_ads_touch || {};
      var touches = [google.params, stored.first_touch, stored.last_touch];
      return touches.some(function (touch) {
        if (!touch) return false;
        if (touch.gclid || touch.gbraid || touch.wbraid) return true;
        return /^(cpc|ppc|paid|paidsearch|paid_search|paid_social|paidsocial|cpm|cpv|display)$/i
          .test(String(touch.utm_medium || '').trim());
      }) ? 1 : 0;
    } catch (e) {
      log('[TruthLedger] Error getting paid flag:', e);
      return 'uncertain';
    }
  }

  /**
   * Get the type of click ID present
   * Returns: 'gclid', 'gbraid', 'wbraid', or 'none'
   */
  function getClickIdType() {
    try {
      var stored = AttributionStorage.read();
      if (!stored) return 'none';
      var google = stored.last_google_ads_touch || {};
      var touches = [google.params, stored.last_touch, stored.first_touch];
      for (var i = 0; i < touches.length; i++) {
        var touch = touches[i] || {};
        if (touch.gclid) return 'gclid';
        if (touch.gbraid) return 'gbraid';
        if (touch.wbraid) return 'wbraid';
      }
      return 'none';
    } catch (e) {
      log('[TruthLedger] Error getting click ID type:', e);
      return 'none';
    }
  }

  /**
   * Get current consent status
   * Returns: 'granted', 'denied', 'unknown'
   */
  function getConsentStatus() {
    try {
      var stored = AttributionStorage.read();
      if (!stored) return 'unknown';

      return stored.consentMeta?.status || 'unknown';
    } catch (e) {
      log('[TruthLedger] Error getting consent status:', e);
      return 'unknown';
    }
  }

  /**
   * Get all Truth Ledger fields as a single object
   * Useful for sending to GA4
   */
  function getAllFields() {
    return {
      tl_paid_flag: getPaidFlag(),
      tl_click_id_type: getClickIdType(),
      tl_consent_status: getConsentStatus(),
    };
  }

  return {
    getPaidFlag: getPaidFlag,
    getClickIdType: getClickIdType,
    getConsentStatus: getConsentStatus,
    getAllFields: getAllFields
  };

})();

/**
 * Links existing Truth Ledger IDs to the installed Clarity recorder.
 * Consentmo analytics consent is required. No IDs are created here.
 */
var AttributionClarity = window.AttributionClarity || (function () {
  'use strict';

  var lastCall = null;
  var state = 'waiting';
  var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  function sync() {
    if (document.visibilityState === 'hidden') return;

    try {
      var cmp = window.Consentmo;
      var consent = cmp && typeof cmp.currentVisitorConsent === 'function'
        ? cmp.currentVisitorConsent()
        : null;

      if (!consent || consent.analytics !== true) {
        lastCall = null;
        state = 'waiting_for_analytics_consent';
        return;
      }

      var clarity = window.clarity;
      // The loaded Clarity dispatcher has .v; the waiting queue does not.
      if (typeof clarity !== 'function' ||
          typeof clarity.v !== 'string' || !clarity.v) {
        lastCall = null;
        state = 'waiting_for_clarity';
        return;
      }

      var storage = window.AttributionStorage;
      var stored = storage && typeof storage.read === 'function'
        ? storage.read()
        : null;
      var userId = stored && stored.lmj_user_id;
      var sessionId = stored && stored.lmj_session_id;

      if (typeof userId !== 'string' || !UUID.test(userId) ||
          typeof sessionId !== 'string' || !UUID.test(sessionId)) {
        lastCall = null;
        state = 'waiting_for_lmj_ids';
        return;
      }

      var samePair = lastCall && lastCall.clarity === clarity &&
        lastCall.userId === userId && lastCall.sessionId === sessionId;
      if (samePair && (state !== 'error' || lastCall.attempts >= 3 ||
          Date.now() < lastCall.retryAt)) {
        return;
      }

      var call = {
        clarity: clarity,
        userId: userId,
        sessionId: sessionId,
        attempts: samePair ? lastCall.attempts + 1 : 1,
        retryAt: 0
      };
      lastCall = call;
      var result = clarity('identify', userId, sessionId);
      state = 'submitted';

      // Submission is not proof of delivery; verify in Clarity recordings.
      if (result && typeof result.catch === 'function') {
        result.catch(function () {
          if (lastCall === call) {
            state = 'error';
            call.retryAt = Date.now() + 5000;
          }
        });
      }
    } catch (e) {
      state = 'error';
      if (call && lastCall === call) call.retryAt = Date.now() + 5000;
    }
  }

  function scheduleSync() {
    // Let the consent manager finish its current update first.
    window.setTimeout(sync, 0);
  }

  document.addEventListener('DOMContentLoaded', scheduleSync);
  document.addEventListener('consentmoSignal_onLoad', scheduleSync);
  document.addEventListener('consentmoSignal', scheduleSync);
  document.addEventListener('visitorConsentCollected', scheduleSync);
  document.addEventListener('visibilitychange', scheduleSync);
  window.addEventListener('load', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);

  // Handles late Clarity loading and consent changes without extra ID calls.
  window.setInterval(sync, 1000);
  scheduleSync();

  return {
    getStatus: function () {
      return {
        status: state,
        lmj_user_id: lastCall ? lastCall.userId : null,
        lmj_session_id: lastCall ? lastCall.sessionId : null
      };
    }
  };
})();

/* LMJ GA4 / Clarity identity link v1.0 — 30 Sep 2026.
 * Append to attribution.tl-events.js. Uses existing Google tag and LMJ IDs.
 * No new cookies, tag loader, GA4 config, ecommerce events or consent changes.
 */
;(function () {
  'use strict';
  if (window.LMJGA4ClarityLink) return;
  var target = 'G-BFDDPZ7R5N';
  var uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var sent = Object.create(null);
  var pending = null;
  var retryAt = 0;
  var state = 'waiting';
  var lastPair = null;

  function pair() {
    try {
      if (document.visibilityState === 'hidden') return null;
      var consent = window.Consentmo && window.Consentmo.currentVisitorConsent();
      if (!consent || consent.analytics !== true) return null;
      var data = window.AttributionStorage && window.AttributionStorage.read();
      if (!data || !uuid.test(data.lmj_user_id || '') || !uuid.test(data.lmj_session_id || '')) return null;
      return { user: data.lmj_user_id, session: data.lmj_session_id,
        key: data.lmj_user_id + ':' + data.lmj_session_id };
    } catch (_) { return null; }
  }

  function clarityMatches(p) {
    try {
      var status = window.AttributionClarity && window.AttributionClarity.getStatus();
      return status && status.status === 'submitted' &&
        status.lmj_user_id === p.user && status.lmj_session_id === p.session;
    } catch (_) { return false; }
  }

  function command() { window.dataLayer.push(arguments); }
  function sync() {
    var p = pair();
    if (!p) { pending = null; state = 'waiting_for_consent_or_ids'; return; }
    if (sent[p.key]) { state = 'queued'; return; }
    if (!clarityMatches(p)) { state = 'waiting_for_clarity'; return; }
    if (!Array.isArray(window.dataLayer)) { state = 'waiting_for_google'; return; }
    var now = Date.now();
    if (pending && pending.key === p.key && now < pending.expires) return;
    if (now < retryAt) return;
    var attempt = { key: p.key, expires: now + 10000 };
    pending = attempt;
    retryAt = now + 30000;
    state = 'waiting_for_google';
    function valid() {
      var current = pair();
      return pending === attempt && Date.now() <= attempt.expires &&
        current && current.key === p.key && clarityMatches(current) && !sent[p.key];
    }
    command('get', target, 'client_id', function (cid) {
      if (!cid || !valid()) return;
      command('get', target, 'session_id', function (sid) {
        if (!sid || !valid()) return;
        sent[p.key] = true;
        pending = null;
        lastPair = { lmj_user_id: p.user, lmj_session_id: p.session };
        state = 'queued';
        command('event', 'lmj_identity_link', {
          send_to: target,
          lmj_user_id: p.user,
          lmj_session_id: p.session,
          page_location: window.location.origin + window.location.pathname
        });
      });
    });
  }

  window.LMJGA4ClarityLink = {
    version: '1.0',
    sync: sync,
    getStatus: function () {
      return { status: state, lmj_user_id: lastPair && lastPair.lmj_user_id,
        lmj_session_id: lastPair && lastPair.lmj_session_id };
    }
  };
  ['DOMContentLoaded', 'consentmoSignal_onLoad', 'consentmoSignal',
    'visitorConsentCollected', 'visibilitychange'].forEach(function (name) {
    document.addEventListener(name, sync);
  });
  window.addEventListener('pageshow', sync);
  window.setInterval(sync, 1000);
  sync();
}());

;(function () {
  'use strict';
  if (window.LMJGalleryTracking) return;
  var target = 'G-BFDDPZ7R5N';
  var uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var bound = new WeakSet();
  var queued = 0;
  var lastEvent = null;
  var status = 'ready';

  function consentGranted() {
    try {
      return document.visibilityState !== 'hidden' &&
        window.Consentmo.currentVisitorConsent().analytics === true;
    } catch (_) { return false; }
  }

  function ids() {
    try {
      var value = window.AttributionStorage.read();
      if (value && uuid.test(value.lmj_user_id || '') && uuid.test(value.lmj_session_id || '')) {
        return { user: value.lmj_user_id, session: value.lmj_session_id };
      }
    } catch (_) {}
    return null;
  }

  function command() { window.dataLayer.push(arguments); }

  function send(payload) {
    if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
    if (!Array.isArray(window.dataLayer)) { status = 'google_unavailable'; return; }
    var identity = ids();
    if (identity) {
      payload.lmj_user_id = identity.user;
      payload.lmj_session_id = identity.session;
    }
    payload.send_to = target;
    payload.page_location = window.location.origin + window.location.pathname;
    var expires = Date.now() + 5000;
    var completed = false;
    status = 'waiting_for_google';
    command('get', target, 'client_id', function (cid) {
      if (completed || !cid || Date.now() > expires || !consentGranted()) return;
      var current = ids();
      if (identity && (!current || current.user !== identity.user || current.session !== identity.session)) return;
      completed = true;
      command('event', 'gallery_image_select', payload);
      queued += 1;
      lastEvent = Object.assign({}, payload);
      status = 'queued';
    });
  }

  function bind(gallery) {
    if (bound.has(gallery)) return;
    var product = gallery.closest('product-info[data-product-id]');
    if (!product) return;
    bound.add(gallery);
    var intent = null;
    var start = null;

    function arm(method, expected, source) {
      if (!consentGranted()) return;
      var token = { method: method, expected: expected, source: source };
      intent = token;
      window.setTimeout(function () { if (intent === token) intent = null; }, 0);
    }

    gallery.addEventListener('click', function (event) {
      if (!event.isTrusted || !(event.target instanceof Element)) return;
      var thumb = event.target.closest('[data-thumb-link]');
      if (thumb) {
        arm(event.detail === 0 ? 'thumbnail_keyboard' : 'thumbnail', thumb.getAttribute('data-media-id'), 'thumbnail');
      } else if (event.target.closest('[data-button-next]')) {
        arm('next_arrow', null, 'gallery');
      } else if (event.target.closest('[data-button-prev]')) {
        arm('previous_arrow', null, 'gallery');
      }
    }, true);

    gallery.addEventListener('keyup', function (event) {
      if (!event.isTrusted || !(event.target instanceof Element) ||
          !event.target.closest('[data-product-media-list]')) return;
      if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') arm('keyboard_arrow', null, 'gallery');
    }, true);

    function point(event) {
      var touch = event.changedTouches && event.changedTouches[0];
      return { x: touch ? touch.screenX : event.pageX, y: touch ? touch.screenY : event.pageY };
    }
    function down(event) {
      if (!event.isTrusted || !(event.target instanceof Element) ||
          !event.target.closest('[data-product-media-list]')) return;
      start = point(event);
    }
    function up(event) {
      if (!start || !event.isTrusted) return;
      var end = point(event);
      var dx = Math.abs(end.x - start.x);
      var dy = Math.abs(end.y - start.y);
      start = null;
      if (dx > 10 && dx > dy) arm(event.type === 'touchend' ? 'swipe' : 'drag', null, 'gallery');
    }
    gallery.addEventListener('mousedown', down, true);
    gallery.addEventListener('touchstart', down, { capture: true, passive: true });
    gallery.addEventListener('mouseup', up, true);
    gallery.addEventListener('touchend', up, { capture: true, passive: true });
    gallery.addEventListener('mouseleave', function () { start = null; });
    gallery.addEventListener('touchcancel', function () { start = null; });

    gallery.addEventListener('theme:media:select', function (event) {
      var chosen = intent;
      intent = null;
      if (!chosen || !event.detail || !consentGranted()) return;
      if (chosen.source === 'gallery' && event.target !== gallery) return;
      if (chosen.source === 'thumbnail' && !(event.target instanceof Element && event.target.matches('product-thumbs'))) return;
      var mediaKey = String(event.detail.id || '');
      if (chosen.expected && chosen.expected !== mediaKey) return;
      if (gallery.getAttribute('data-active-media') === mediaKey) return;
      var media = Array.prototype.slice.call(gallery.querySelectorAll('[data-image-id][data-media-id]'));
      var selected = media.find(function (item) { return item.getAttribute('data-media-id') === mediaKey; });
      if (!selected || selected.getAttribute('data-type') !== 'image') return;
      var payload = {
        product_id: product.getAttribute('data-product-id'),
        product_handle: product.getAttribute('data-product-handle'),
        image_id: selected.getAttribute('data-image-id'),
        image_position: media.indexOf(selected) + 1,
        image_count: media.filter(function (item) { return item.getAttribute('data-type') === 'image'; }).length,
        interaction_method: chosen.method,
        gallery_context: 'product_gallery'
      };
      queueMicrotask(function () {
        if (gallery.isConnected && gallery.getAttribute('data-active-media') === mediaKey) send(payload);
      });
    }, true);
  }

  function scan() {
    document.querySelectorAll('product-images').forEach(bind);
  }
  window.LMJGalleryTracking = {
    version: '1.0',
    getStatus: function () { return { status: status, queued: queued, last_event: lastEvent }; }
  };
  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('theme:product-info:loaded', scan);
  document.addEventListener('theme:html:change', scan);
  document.addEventListener('shopify:section:load', scan);
  scan();
}());

;(function () {
  'use strict';
  if (window.LMJSizeGuideTracking) return;
  var target = 'G-BFDDPZ7R5N';
  var uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var queued = 0;
  var lastEvent = null;
  var status = 'ready';

  function consentGranted() {
    try {
      return document.visibilityState !== 'hidden' &&
        window.Consentmo.currentVisitorConsent().analytics === true;
    } catch (_) { return false; }
  }

  function ids() {
    try {
      var value = window.AttributionStorage.read();
      if (value && uuid.test(value.lmj_user_id || '') && uuid.test(value.lmj_session_id || '')) {
        return { user: value.lmj_user_id, session: value.lmj_session_id };
      }
    } catch (_) {}
    return null;
  }

  function command() { window.dataLayer.push(arguments); }

  function send(payload) {
    if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
    if (!Array.isArray(window.dataLayer)) { status = 'google_unavailable'; return; }
    var identity = ids();
    if (identity) {
      payload.lmj_user_id = identity.user;
      payload.lmj_session_id = identity.session;
    }
    payload.send_to = target;
    payload.page_location = window.location.origin + window.location.pathname;
    var expires = Date.now() + 5000;
    var completed = false;
    status = 'waiting_for_google';
    command('get', target, 'client_id', function (cid) {
      if (completed || !cid || Date.now() > expires || !consentGranted()) return;
      var current = ids();
      if (identity && (!current || current.user !== identity.user || current.session !== identity.session)) return;
      completed = true;
      command('event', 'size_guide_open', payload);
      queued += 1;
      lastEvent = Object.assign({}, payload);
      status = 'queued';
    });
  }

  document.addEventListener('click', function (event) {
    try {
      if (!event.isTrusted || event.button !== 0 || event.ctrlKey || event.metaKey ||
          event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
      var link = event.target.closest('a[data-popup-open]');
      if (!link) return;
      var url = new URL(link.getAttribute('href'), window.location.href);
      if (url.origin !== window.location.origin || url.pathname.replace(/\/$/, '') !== '/pages/jewellery-sizing') return;
      var popup = link.closest('popup-component');
      var product = link.closest('product-info[data-product-id]');
      var dialog = popup && popup.querySelector('dialog');
      if (!product || !dialog || dialog.hasAttribute('open')) return;
      if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
      var payload = {
        product_id: product.getAttribute('data-product-id'),
        product_handle: product.getAttribute('data-product-handle'),
        interaction_method: event.detail === 0 ? 'keyboard' : 'click_or_tap',
        guide_path: url.pathname
      };
      window.setTimeout(function () {
        if (dialog.isConnected && dialog.hasAttribute('open') &&
            dialog.getAttribute('aria-hidden') !== 'true' && !dialog.hasAttribute('inert')) send(payload);
      }, 0);
    } catch (_) { status = 'error'; }
  }, true);

  window.LMJSizeGuideTracking = {
    version: '1.0',
    getStatus: function () { return { status: status, queued: queued, last_event: lastEvent }; }
  };
}());

;(function () {
  'use strict';
  if (window.LMJSizeSelectionTracking) return;
  var target = 'G-BFDDPZ7R5N';
  var uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var queued = 0;
  var lastEvent = null;
  var status = 'ready';
  var pending = new WeakMap();

  function consentGranted() {
    try {
      return document.visibilityState !== 'hidden' &&
        window.Consentmo.currentVisitorConsent().analytics === true;
    } catch (_) { return false; }
  }

  function ids() {
    try {
      var value = window.AttributionStorage.read();
      if (value && uuid.test(value.lmj_user_id || '') && uuid.test(value.lmj_session_id || '')) {
        return { user: value.lmj_user_id, session: value.lmj_session_id };
      }
    } catch (_) {}
    return null;
  }

  function command() { window.dataLayer.push(arguments); }

  function send(payload) {
    if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
    if (!Array.isArray(window.dataLayer)) { status = 'google_unavailable'; return; }
    var identity = ids();
    if (identity) {
      payload.lmj_user_id = identity.user;
      payload.lmj_session_id = identity.session;
    }
    payload.send_to = target;
    payload.page_location = window.location.origin + window.location.pathname;
    var expires = Date.now() + 5000;
    var completed = false;
    status = 'waiting_for_google';
    command('get', target, 'client_id', function (cid) {
      if (completed || !cid || Date.now() > expires || !consentGranted()) return;
      var current = ids();
      if (identity && (!current || current.user !== identity.user || current.session !== identity.session)) return;
      completed = true;
      command('event', 'size_select', payload);
      queued += 1;
      lastEvent = Object.assign({}, payload);
      status = 'queued';
    });
  }

  function selected(product) {
    try {
      var node = product.querySelector('variant-selects [data-selected-variant]');
      return node ? JSON.parse(node.textContent) : null;
    } catch (_) { return null; }
  }

  function arm(control, value, method) {
    var product = control.closest('product-info[data-product-id]');
    var wrapper = control.closest('[data-option-position]');
    if (!product || !wrapper || !control.closest('variant-selects')) return;
    pending.delete(product);
    var label = wrapper.querySelector('.radio__legend__option-name');
    if (!label || !/\bsize\b/i.test(label.textContent)) return;
    var position = Number(wrapper.getAttribute('data-option-position'));
    if (!Number.isInteger(position) || position < 1 || position > 3 || !value) return;
    var before = selected(product);
    if (before && before['option' + position] === value) return;
    if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
    var identity = ids();
    var token = {};
    var expires = Date.now() + 5000;
    pending.set(product, token);
    status = 'waiting_for_variant';

    function check() {
      if (pending.get(product) !== token) return;
      var currentIds = ids();
      if (!product.isConnected || !consentGranted() ||
          (identity && (!currentIds || identity.user !== currentIds.user || identity.session !== currentIds.session))) {
        pending.delete(product);
        status = 'cancelled';
        return;
      }
      var variant = selected(product);
      var input = product.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (variant && variant.id && variant['option' + position] === value &&
          input && String(input.value) === String(variant.id)) {
        pending.delete(product);
        send({
          product_id: product.getAttribute('data-product-id'),
          product_handle: product.getAttribute('data-product-handle'),
          variant_id: String(variant.id),
          size: value,
          previous_size: before ? String(before['option' + position] || '') : '',
          option_name: label.textContent.trim(),
          variant_available: variant.available === true ? 'yes' : 'no',
          interaction_method: method
        });
        return;
      }
      if (Date.now() >= expires) {
        pending.delete(product);
        status = 'variant_not_confirmed';
        return;
      }
      window.setTimeout(check, 50);
    }
    window.setTimeout(check, 0);
  }

  document.addEventListener('click', function (event) {
    try {
      if (!event.isTrusted || event.button !== 0 || event.ctrlKey || event.metaKey ||
          event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
      var choice = event.target.closest('variant-selects [data-popout-option]');
      if (choice) arm(choice, choice.getAttribute('data-value'), event.detail === 0 ? 'keyboard' : 'click_or_tap');
    } catch (_) { status = 'error'; }
  }, true);

  document.addEventListener('change', function (event) {
    try {
      if (!event.isTrusted || !(event.target instanceof Element)) return;
      var control = event.target;
      if (control.matches('variant-selects select, variant-selects input[type="radio"]')) {
        arm(control, control.value, 'native_control');
      }
    } catch (_) { status = 'error'; }
  }, true);

  window.LMJSizeSelectionTracking = {
    version: '1.0',
    getStatus: function () { return { status: status, queued: queued, last_event: lastEvent }; }
  };
}());

;(function () {
  'use strict';
  if (window.LMJPolicyTracking) return;
  var target = 'G-BFDDPZ7R5N';
  var uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var queued = 0;
  var lastEvent = null;
  var status = 'ready';
  var pending = new WeakMap();

  function consentGranted() {
    try {
      return document.visibilityState !== 'hidden' &&
        window.Consentmo.currentVisitorConsent().analytics === true;
    } catch (_) { return false; }
  }

  function ids() {
    try {
      var value = window.AttributionStorage.read();
      if (value && uuid.test(value.lmj_user_id || '') && uuid.test(value.lmj_session_id || '')) {
        return { user: value.lmj_user_id, session: value.lmj_session_id };
      }
    } catch (_) {}
    return null;
  }

  function sameIds(a, b) {
    return a && b && a.user === b.user && a.session === b.session;
  }

  function visible(element) {
    if (!element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    var style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  function command() { window.dataLayer.push(arguments); }

  function send(payload, identity) {
    if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
    if (!sameIds(identity, ids())) { status = 'cancelled'; return; }
    if (!Array.isArray(window.dataLayer)) { status = 'google_unavailable'; return; }
    payload.lmj_user_id = identity.user;
    payload.lmj_session_id = identity.session;
    payload.send_to = target;
    payload.page_location = window.location.origin + window.location.pathname;
    var completed = false;
    var expires = Date.now() + 5000;
    status = 'waiting_for_google';
    window.setTimeout(function () {
      if (!completed) {
        completed = true;
        status = 'google_timeout';
      }
    }, 5000);
    command('get', target, 'client_id', function (cid) {
      if (completed || !cid || Date.now() >= expires) return;
      completed = true;
      if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
      if (!sameIds(identity, ids())) { status = 'cancelled'; return; }
      command('event', 'product_policy_open', payload);
      queued += 1;
      lastEvent = Object.assign({}, payload);
      status = 'queued';
    });
  }

  function handle(event) {
    try {
      if (!event.isTrusted || !(event.target instanceof Element) ||
          event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (event.type === 'click' && event.button !== 0) return;
      if (event.type !== 'click' && (event.repeat || (event.key !== 'Enter' && event.key !== ' '))) return;
      var control = event.target.closest('tabs-component .tab-link[data-tab][data-block-id]');
      if (!control) return;
      var component = control.closest('tabs-component');
      pending.delete(component);
      var blockId = control.getAttribute('data-block-id');
      if (!blockId || blockId.indexOf('faq_tab_') !== 0) return;
      var label = control.textContent.replace(/\s+/g, ' ').trim();
      var topic = /^(shipping|delivery)$/i.test(label) ? 'shipping' :
        /^returns\s*(?:&|and)\s*exchanges$/i.test(label) ? 'returns_exchanges' : null;
      if (!topic || control.classList.contains('current')) return;
      var panel = document.getElementById('tab--' + blockId);
      if (!panel || !component.contains(panel)) return;
      var product = document.querySelector('#MainContent product-info[data-product-id][data-product-handle]');
      var route = window.location.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!product || !route || decodeURIComponent(route[1]) !== product.getAttribute('data-product-handle')) return;
      if (!consentGranted()) { status = 'suppressed_by_consent'; return; }
      var identity = ids();
      if (!identity) { status = 'missing_lmj_ids'; return; }
      var token = {};
      var expires = Date.now() + 1500;
      pending.set(component, token);
      status = 'waiting_for_panel';
      function check() {
        try {
          if (pending.get(component) !== token) return;
          if (!control.isConnected || !panel.isConnected || !consentGranted() || !sameIds(identity, ids())) {
            pending.delete(component);
            status = 'cancelled';
            return;
          }
          if (control.classList.contains('current') && panel.classList.contains('current') && visible(panel)) {
            pending.delete(component);
            send({
              product_id: product.getAttribute('data-product-id'),
              product_handle: product.getAttribute('data-product-handle'),
              policy_topic: topic,
              policy_label: label,
              policy_context: 'product_faq_tab',
              interaction_method: event.type !== 'click' || event.detail === 0 ? 'keyboard' : 'click_or_tap'
            }, identity);
            return;
          }
          if (Date.now() >= expires) {
            pending.delete(component);
            status = 'panel_not_confirmed';
            return;
          }
          window.setTimeout(check, 50);
        } catch (_) { pending.delete(component); status = 'error'; }
      }
      window.setTimeout(check, 0);
    } catch (_) { status = 'error'; }
  }

  document.addEventListener('click', handle, true);
  document.addEventListener('keydown', handle, true);
  document.addEventListener('keyup', handle, true);
  window.LMJPolicyTracking = {
    version: '1.0',
    getStatus: function () { return { status: status, queued: queued, last_event: lastEvent }; }
  };
}());